import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { IntlService } from 'ember-intl';
import type { LinkManagerService } from 'ember-link';
import type { Tab } from 'walden-fast/components/tabs';
import type { TraceOrderState } from 'walden-fast/types/trace-order';
import { SERVICE_NAMES } from 'walden-fast/utils/constants/services';
import { t } from 'walden-fast/utils/intl';
import type { ShowcaseContentTab } from 'walden-fast/components/showcase-content';
import type { AppServicesWaldenTraceModel } from 'walden-fast/routes/app/services/walden-trace';

interface ViewConfig {
  pageView: number;
  trackingCode: string;
}

const Views: Record<string, ViewConfig[]> = {
  'movianto-transport': [
    { pageView: 5, trackingCode: 'MOV-OR' },
    { pageView: 5, trackingCode: 'MOV-OP' },
    { pageView: 5, trackingCode: 'MOV-TOR' },
    { pageView: 5, trackingCode: 'MOV-SIP' },
    { pageView: 5, trackingCode: 'MOV-D' },
  ],
  'etp-transport': [
    { pageView: 3, trackingCode: 'ETP-TOR' },
    { pageView: 3, trackingCode: 'ETP-SIP' },
    { pageView: 3, trackingCode: 'ETP-D' },
  ],
};

const reportUrls: Record<string, string> = {
  'en-us':
    'https://waldendigital-demo-bdb4h3bjdydrdcbe.westeurope-01.azurewebsites.net',
  'fr-fr': 'https://waldendigital-demo-fr.azurewebsites.net',
  'de-de': 'https://waldendigital-demo-de.azurewebsites.net',
  'es-es':
    'https://waldendigital-demo-bdb4h3bjdydrdcbe.westeurope-01.azurewebsites.net',
};

const apiEndpoints: Record<string, string> = {
  'en-us':
    'https://logic-walden-trace-001.azurewebsites.net/api/waldendigital-track-and-trace-notification/triggers/API_Trigger/invoke?api-version=2022-05-01&sp=%2Ftriggers%2FAPI_Trigger%2Frun&sv=1.0&sig=84zE9TM6vOGMe-1vb3h4L1F34Z-0G71TYGOpJeIejkE',
  'fr-fr':
    'https://logic-walden-trace-001.azurewebsites.net:443/api/waldendigital-track-and-trace-notification-FR/triggers/API_Trigger/invoke?api-version=2022-05-01&sp=%2Ftriggers%2FAPI_Trigger%2Frun&sv=1.0&sig=COdu9rwoViYkntg37QzHXIRhtb1kUF55xvVuK-4jXHo',
  'de-de':
    'https://logic-walden-trace-001.azurewebsites.net:443/api/waldendigital-track-and-trace-notification-DE/triggers/API_Trigger/invoke?api-version=2022-05-01&sp=%2Ftriggers%2FAPI_Trigger%2Frun&sv=1.0&sig=irenNtt16nuPlewWTyFMvafls7GE_EFXD6E1Na0jUuE',
  'es-es':
    'https://logic-walden-trace-001.azurewebsites.net/api/waldendigital-track-and-trace-notification/triggers/API_Trigger/invoke?api-version=2022-05-01&sp=%2Ftriggers%2FAPI_Trigger%2Frun&sv=1.0&sig=84zE9TM6vOGMe-1vb3h4L1F34Z-0G71TYGOpJeIejkE',
};

const translationKeyMap: Record<TraceOrderState, string> = {
  received: t('pages.waldenTrace.orderState.received'),
  planned: t('pages.waldenTrace.orderState.planned'),
  'out-for-delivery': t('pages.waldenTrace.orderState.outForDelivery'),
  delivered: t('pages.waldenTrace.orderState.delivered'),
};

export default class AppServicesWaldenTraceController extends Controller {
  declare model: AppServicesWaldenTraceModel;

  serviceName = SERVICE_NAMES.WALDEN_TRACE;

  @service('intl') declare intlService: IntlService;
  @service('link-manager') declare linkManager: LinkManagerService;

  get tabs(): Tab[] {
    return [
      {
        internalLink: this.linkManager.createLink({
          route: 'app.services.walden-trace',
          models: ['movianto-transport'],
        }),
        label: this.intlService.t('pages.waldenTrace.tabs.movianto'),
        type: 'internal',
      },
      {
        internalLink: this.linkManager.createLink({
          route: 'app.services.walden-trace',
          models: ['etp-transport'],
        }),
        label: this.intlService.t('pages.waldenTrace.tabs.etp'),
        type: 'internal',
      },
    ];
  }

  @tracked
  activeStep: number = 1;

  @tracked
  email: string = '';

  @tracked
  phone: string = '';

  @action
  previousStep() {
    this.activeStep -= 1;
  }

  @action
  nextStep() {
    this.activeStep += 1;
  }

  @action
  async sendExample(event: Event) {
    event.preventDefault();
    await this.callTrace.perform();
  }

  callTrace = task(async () => {
    const apiUrl = apiEndpoints[this.locale] as string;

    return fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: this.email,
        guid: this.shownConfig?.trackingCode,
        phone: this.phone,
      }),
    })
      .then(() => {
        // returns http 502, but can be ignored until fixed: https://verhaertdigital.atlassian.net/browse/WGF-98
        window.alert(
          this.intlService.t('pages.waldenTrace.alert.sent', {
            email: this.email,
            phone: this.phone,
          }),
        );
      })
      .catch((e) => {
        console.error(e);
        window.alert(this.intlService.t('pages.waldenTrace.alert.sentFailed'));
      });
  });

  orderTranslation = (state: TraceOrderState) => {
    return this.intlService.t(translationKeyMap[state]);
  };

  get locale() {
    if (this.intlService.primaryLocale === 'en') {
      return 'en-us';
    }
    return this.intlService.primaryLocale?.toLocaleLowerCase() || 'en-us';
  }

  get shownConfig(): ViewConfig | null {
    return this.model.carrier
      ? (Views[this.model.carrier]?.[this.activeStep - 1] as ViewConfig)
      : null;
  }

  get shownView(): string {
    const viewUrl = reportUrls[this.locale];
    return `${viewUrl}/?PageView=${this.shownConfig?.pageView}&TrackingCode=${this.shownConfig?.trackingCode}`;
  }

  get possibleSteps(): number {
    return (this.model.carrier && Views[this.model.carrier]?.length) || 0;
  }

  @action
  reset() {
    this.activeStep = 1;
  }

  get showcaseTabs(): ShowcaseContentTab[] {
    return [
      {
        label: this.intlService.t('pages.waldenTrace.teaser.tabRealtime.label'),
        value: 'realtime',
        iconName: 'alarm-clock-check',
        title: this.intlService.t('pages.waldenTrace.teaser.tabRealtime.title'),
        description: this.intlService.t(
          'pages.waldenTrace.teaser.tabRealtime.description',
        ),
        image: '/assets/images/trace_teaser_1.png',
        primaryCta: {
          type: 'activate-service',
          label: this.intlService.t('pages.waldenTrace.teaser.getTrace'),
          value: '',
        },
      },
      {
        label: this.intlService.t('pages.waldenTrace.teaser.tabTailored.label'),
        value: 'tailored',
        iconName: 'package-search',
        title: this.intlService.t('pages.waldenTrace.teaser.tabTailored.title'),
        description: this.intlService.t(
          'pages.waldenTrace.teaser.tabTailored.description',
        ),
        image: '/assets/images/trace_teaser_2.png',
        primaryCta: {
          type: 'activate-service',
          label: this.intlService.t('pages.waldenTrace.teaser.getTrace'),
          value: '',
        },
      },
      {
        label: this.intlService.t(
          'pages.waldenTrace.teaser.tabProactive.label',
        ),
        value: 'proactive',
        iconName: 'bell-ring',
        title: this.intlService.t(
          'pages.waldenTrace.teaser.tabProactive.title',
        ),
        description: this.intlService.t(
          'pages.waldenTrace.teaser.tabProactive.description',
        ),
        image: '/assets/images/trace_teaser_3.png',
        primaryCta: {
          type: 'activate-service',
          label: this.intlService.t('pages.waldenTrace.teaser.getTrace'),
          value: '',
        },
      },
      {
        label: this.intlService.t(
          'pages.waldenTrace.teaser.tabInteractive.label',
        ),
        value: 'interactive',
        iconName: 'pointer',
        title: this.intlService.t(
          'pages.waldenTrace.teaser.tabInteractive.label',
        ),
        description: this.intlService.t(
          'pages.waldenTrace.teaser.tabInteractive.description',
        ),
        image: '/assets/images/trace_teaser_4.png',
        primaryCta: {
          type: 'activate-service',
          label: this.intlService.t('pages.waldenTrace.teaser.getTrace'),
          value: '',
        },
      },
    ];
  }
}
